/**
 * /* eslint react/prop-types: 1, @typescript-eslint/no-unsafe-assignment: 1, @typescript-eslint/no-unsafe-call: 1, @typescript-eslint/restrict-template-expressions: 1, @typescript-eslint/no-unsafe-return: 1, no-negated-condition: 1
 *
 * @format
 */

import React, {useState, useEffect} from 'react';
import {Box, Container, Flex} from '@chakra-ui/layout';
import Patterns from '../../patterns';
import Layout from '../../components/Layout';
import {getContentTypes} from '../../lib/api/contentTypes';
import {
	getTerms,
	getPostCategories,
	getContentNode,
} from '../../lib/api/contentNodes';
import {getSeoFields} from '../../lib/getSeoFields';
import {NextSeo} from 'next-seo';
import getMainMenu from '../../lib/api/acf/mainMenu';
import mappingMainMenu from '../../patterns/MainMenu/mappingMainMenu';
import getFooter from '../../lib/api/acf/footer';
import mappingFooter from '../../patterns/Footer/mappingFooter';
import WYSIWYG from '../../patterns/WYSIWYG';
import Spacer from '../../patterns/Spacer';
import {spacerMapping} from '../../patterns/Spacer/spacerMapping';
import FadedContainer from '../../components/FadedContainer';
import {ctaBannerMapping} from '../../patterns/CTABanners/ctaBannerMapping';
import CTABanner from '../../patterns/CTABanners';
import Image from '../../patterns/Image';
import {imageMapping} from '../../patterns/Image/imageMapping';
import {titleTextBannerMapping} from '../../patterns/TitleTextBanner/titleTextBannerMapping';
import TitleTextBanner from '../../patterns/TitleTextBanner';
import useUser from '../../../lib/useUser';
import contentData from '../../data/contentData';
import {useBreakpoint} from '@chakra-ui/media-query';
import BackLink from '../../components/BackLink';
import {textStyles} from '../../styles/js/textStyles';

// eslint-disable-next-line react/prop-types
function DateBadge({post}) {
	const headingTextStyle = textStyles.tagsDatesLight;
	let postDate = null;

	// eslint-disable-next-line react/prop-types
	if (post.date) {
		// eslint-disable-next-line react/prop-types
		postDate = post.date;
		// eslint-disable-next-line react/prop-types
	} else if (post.post.date) {
		// eslint-disable-next-line react/prop-types
		postDate = post.post.date;
	}

	if (postDate) {
		// eslint-disable-next-line react/prop-types
		const date = new Date(postDate);
		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear().toString();
		const formattedDate = `${day}/${month}/${year}`;

		return (
			<Flex
				direction='column'
				alignItems='flex-start'
				// eslint-disable-next-line @typescript-eslint/object-curly-spacing
				sx={{ '> *:last-child': { mb: '0' } }}>
				<Box
					sx={headingTextStyle}
					// eslint-disable-next-line @typescript-eslint/object-curly-spacing
					pr={{ lg: '1.5em' }}
					// eslint-disable-next-line @typescript-eslint/object-curly-spacing
					mb={{ base: '6' }}
					whiteSpace='pre-line'>
					{formattedDate}
				</Box>
			</Flex>
		);
		// eslint-disable-next-line no-else-return
	} else {
		return null;
	}
}

const FullContent = post => {
	if (!post) {
		return;
	}

	const backLink = {
		url: '/news/',
	};

	return (
		<>
			<Container maxW='container.xl' mx='auto'>
				<Container maxW='container.md' ml='0' mr='auto' pt='10' pl='0'>
					{post.post.__typename !== 'GuidanceSheet' && post.post.__typename !== 'Page' && post.post.__typename !== 'Policy' && post.post.__typename !== 'Event' && (
						<>
							<Box mb={{base: '6', md: '8'}}>
								<BackLink backLink={{url: backLink.url}} variant='dark' />
							</Box>
							<Box mb={{base: '6', md: '8'}}>
								<DateBadge post={post} />
							</Box>
						</>
					)}
					{post.post.__typename !== 'Page' && (
						<Box className='WYSIWYG WYSIWYG--default'>
							<h1>{post.post.title}</h1>
						</Box>
					)}
				</Container>
			</Container>
			<Box>
				{/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
				{post.post.pageLayout.flexibleContent.map(
					(post: any, index: number) => (
						<Patterns
							/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
							fieldGroupName={post.__typename}
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							post={post}
							// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
							key={`${post.fieldGroupName}_${index}`}
						/>
					),
				)}
			</Box>
			<Container maxW='container.xl' mx='auto' pl={0}>
				{post.post.content ? (
					<Box mt={10} ml={0} pl={0} mr='auto'>
						{
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							post.post.content ? <WYSIWYG html={post.post.content} /> : null
						}
					</Box>
				) : null}
			</Container>
		</>
	);
};

// eslint-disable-next-line react/prop-types
function Post({post, seo, mainMenu, footer, terms, categories}) {
	if (!post) {
		return null;
	}

	const {user} = useUser();
	const [postType, setPostType] = useState();

	/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, react/prop-types, arrow-body-style */
	const filter = categories?.filter(category => {
		return category.slug === 'members-content';
	});

	useEffect(() => {
		// eslint-disable-next-line react/prop-types
		setPostType(post.__typename);
	}, [post]);

	const PostContent = ({filter, post}: {filter: any; post: any}) => {
		if (filter?.length > 0) {
			// this page is available only to registered members with paid subscriptions

			if (
				user?.isLoggedIn
				&& user?.stripe?.stripeSubscription?.status === 'active'
			) {
				// user is logged in and has at least one active subscription
				/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
				return <FullContent post={post} />;
				/* eslint-disable-next-line no-else-return */
			} else {
				// not logged in, hide content

				/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
				return <PreviewContent data={post} />;
			}
			/* eslint-disable-next-line no-else-return */
		} else if (filter?.length <= 0) {
			/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
			return <FullContent post={post} />;
		}
	};

	const PreviewContent = data => {
		if (!data) {
			return;
		}

		const backLink = {
			url: '/news/',
		};

		const ctaProps = ctaBannerMapping(contentData.protectedContent.post);
		const breakpoint = useBreakpoint({ssr: true});

		/* eslint @typescript-eslint/no-unsafe-call: 1, @typescript-eslint/no-unsafe-assignment: 1, array-callback-return: 1, react/prop-types: 1 */
		return (
			<Box pb={10}>
				{breakpoint === 'base' && <CTABanner {...ctaProps} />}
				<FadedContainer>
					<Box pt={10}>
						<Container maxW='container.xl' mx='auto' px='0'>
							<Container maxW='container.md' ml='0' mr='auto' pt='0' pb='8'>
								{data?.data?.__typename !== 'GuidanceSheet' && data.data.__typename !== 'Page' && data.data.__typename !== 'Policy' && data.data.__typename !== 'Event' && (
									<>
										<Box mb={{base: '6', md: '8'}}>
											<BackLink backLink={{url: backLink.url}} variant='dark' />
										</Box>
										<Box mb={{base: '6', md: '8'}}>
											<DateBadge post={post} />
										</Box>
									</>
								)}
								{data?.data?.__typename !== 'Page' && (
									<Box className='WYSIWYG WYSIWYG--default'>
										<h1>{data?.data?.title}</h1>
									</Box>
								)}
							</Container>
						</Container>

						{data?.data?.pageLayout?.flexibleContent?.map(
							(pattern: any, index: number) => {
								switch (pattern.fieldGroupName) {
									// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
									case `${postType}_Pagelayout_FlexibleContent_TitleTextBanner`: {
										const patternData = titleTextBannerMapping(pattern);
										return <TitleTextBanner key={index} {...patternData} />;
									}

									// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
									case `${postType}_Pagelayout_FlexibleContent_Image`: {
										const patternData = imageMapping(pattern);
										return <Image key={index} {...patternData} />;
									}

									// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
									case `${postType}_Pagelayout_FlexibleContent_Wysiwyg`: {
										return <WYSIWYG key={index} html={pattern.wysiwyg} />;
									}

									// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
									case `${postType}_Pagelayout_FlexibleContent_Spacer`: {
										const patternData = spacerMapping(pattern);
										return <Spacer {...patternData} />;
									}

									default:
										return <WYSIWYG key={index} html={pattern.wysiwyg} />;
								}
							},
						)}
					</Box>
					{

						data.data.content ? (
							<>
								<Box pt={10}>
									{data.data.content.includes('<h1>') ? null : (
										<Container maxW='container.xl' mx='auto' px='0'>
											<Container maxW='container.md' ml='0' mr='auto' pt='10'>
												<Box className='WYSIWYG WYSIWYG--default'>
													<h1>{data.data.title}</h1>
												</Box>
											</Container>
										</Container>
									)}
									<WYSIWYG html={data.data.content} />
								</Box>
							</>
						) : null
					}
				</FadedContainer>
				{breakpoint !== 'base' && <CTABanner {...ctaProps} />}
			</Box>
		);
	};

	return (
		/* eslint-disable-next-line react/prop-types, @typescript-eslint/no-unsafe-assignment */
		<Layout seo={post.seo} mainMenu={mainMenu} footer={footer}>
			<NextSeo {...seo} />

			<PostContent filter={filter} post={post} />
		</Layout>
	);
}

export async function getStaticPaths() {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let contentTypes = await getContentTypes();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	contentTypes = contentTypes?.filter(
		contentType =>
			// contentType.name !== "post" &&
			// eslint-disable-next-line operator-linebreak
			contentType.name !== 'page' &&
			contentType.name !== 'person'
			// eslint-disable-next-line operator-linebreak
			&& contentType.name !== 'attachment' &&
			contentType.name !== 'article'
			&& contentType.name !== 'community',
	);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	const uris = contentTypes
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		.map(contentType => contentType.contentNodes.nodes)
		.flat()
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		.map(({uri}) => uri);
	return {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		paths: uris,
		fallback: 'blocking',
	};
}

export async function getStaticProps({params, preview, previewData}) {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-template-expressions
	const id = preview ? previewData.id : `${params.postType}/${params.slug}`;
	const idType = preview ? 'ID' : 'URI';
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const post = await getContentNode(id, idType, preview);
	// eslint-disable-next-line no-negated-condition
	const seo = !preview ? getSeoFields(post) : null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const terms = await getTerms(id, idType, preview);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const categories = (await getPostCategories(id, idType)) || [];

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let mainMenu = await getMainMenu();
	mainMenu = mappingMainMenu(mainMenu);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let footer = await getFooter();
	footer = mappingFooter(footer);

	return {
		props: {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			post,
			seo,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			mainMenu,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			footer,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			terms: terms || null,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			categories,
		},
		revalidate: 60,
	};
}

export default Post;
